import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowLeftRight, Languages, Loader2 } from 'lucide-react';
import Alert from '../components/Alert';
import { TranslationAPI, SessionStore } from '../services/api';

const TranslatePage = () => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Load direction from sessionStorage or default to nl-to-srn
  const [direction, setDirection] = useState(() => {
    return sessionStorage.getItem('translationDirection') || 'nl-to-srn';
  });
  const navigate = useNavigate();

  // Save direction to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('translationDirection', direction);
  }, [direction]);

  const toggleDirection = () => {
    setDirection(prev => prev === 'nl-to-srn' ? 'srn-to-nl' : 'nl-to-srn');
    setInputText(''); // Clear input when switching
  };

  const sourceLanguage = direction === 'nl-to-srn' ? 'Nederlands' : 'Sranantongo';
  const targetLanguage = direction === 'nl-to-srn' ? 'Sranantongo' : 'Nederlands';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputText.trim()) {
      setError(t('translation.errorEmpty'));
      return;
    }

    if (inputText.trim().length > 2000) {
      setError(t('translation.errorTooLong'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await TranslationAPI.translate([inputText.trim()], 512, direction);
      
      if (data.results && data.results.length > 0) {
        // Store translation data in sessionStorage for the results page
        const translationData = {
          sourceText: inputText.trim(),
          translatedText: data.results[0].translated_text,
          originalTranslation: data.results[0].original_translation || undefined, // For post-edit endpoint
          corrections: data.results[0].corrections || undefined, // For post-edit endpoint
          modelUsed: data.model_used,
          processingTime: data.processing_time_ms,
          timestamp: new Date().toISOString(),
          translationId: data.translation_id || null,
          direction: direction, // Store direction for results page
        };
        
        SessionStore.storeTranslation(translationData);
        navigate('/results');
      } else {
        throw new Error('No translation results received');
      }
    } catch (err) {
      console.error('Translation error:', err);
      setError(err.message || t('translation.errorTranslation'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {sourceLanguage} naar {targetLanguage} Vertaling
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {direction === 'nl-to-srn' 
            ? t('translation.subtitleNlToSrn')
            : t('translation.subtitleSrnToNl')
          }
        </p>
      </div>

      {/* Direction Selector */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center bg-white rounded-full shadow-md border border-gray-200 p-1">
          <button
            type="button"
            onClick={() => setDirection('nl-to-srn')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              direction === 'nl-to-srn'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Nederlands → Sranantongo
          </button>
          <button
            type="button"
            onClick={toggleDirection}
            className="p-2 mx-1 text-gray-400 hover:text-primary-600 transition-colors"
            title="Wissel richting"
          >
            <ArrowLeftRight size={20} />
          </button>
          <button
            type="button"
            onClick={() => setDirection('srn-to-nl')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              direction === 'srn-to-nl'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sranantongo → Nederlands
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert 
          type="error" 
          message={error} 
          onClose={() => setError(null)}
          className="mb-6"
        />
      )}

      {/* Translation Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <Languages className="text-primary-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">
              Voer {sourceLanguage} Tekst In
            </h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="source-text" className="block text-sm font-medium text-gray-700 mb-2">
                {sourceLanguage} Tekst ({sourceLanguage})
              </label>
              <textarea
                id="source-text"
                placeholder={`Voer hier uw ${sourceLanguage.toLowerCase()} tekst in...`}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                rows={8}
                className="textarea-field"
                disabled={isLoading}
                maxLength={2000}
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-gray-500">
                  {t('translation.maxCharacters')}
                </p>
                <p className="text-sm text-gray-500">
                  {inputText.length}/2000
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <ArrowRight className="text-primary-600" size={24} />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">
                  {targetLanguage} Output
                </span>
              </div>
              <p className="text-sm text-gray-500 italic">
                Je vertaalde tekst verschijnt hier na het klikken op "Vertalen"
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isLoading || !inputText.trim()}
            className="btn-primary px-8 py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>{t('translation.translating')}</span>
              </>
            ) : (
              <>
                <Languages size={20} />
                <span>Vertalen naar {targetLanguage}</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          {t('translation.about.title')}
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            • {t('translation.about.nllb')}
          </p>
          <p>
            • Geoptimaliseerd specifiek voor {sourceLanguage} naar {targetLanguage} vertaling
          </p>
          <p>
            • {t('translation.about.beta')}
          </p>
          <p>
            • {t('translation.about.quality')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TranslatePage;